/**
 * Signin — tenant-grouped demo sign-in (workforce spec 2026-07-16 §3):
 * pick an organization (bank / university / retail estate…), then a persona
 * within it. Personas map to real role-scoped sessions via demoSignIn →
 * whoami (which now carries per-process `assignments`).
 *
 * Data: getDemoTenants (declared op — GET /demo/tenants). Until it ships, the
 * screen falls back to the existing flat GET /demo/personas chooser, so
 * nothing regresses. Works phone → desktop.
 */
import { useEffect, useState, type CSSProperties } from "react";
import { Btn, Icon, IconBtn, Spinner, T } from "@v0y4ge/jrni-ui";
import { ProgressSpinner } from "./ProgressSpinner";
import { toast } from "./toast";
import { getSessionExpiry, takeSessionReturn, getReauth, finishReauth } from "./access";
import { fetchPersonas, signIn, landingResolved, useAuth, initialsOf, colorFor, getActiveOrganizationId, getUser, can, safeImageUrl, type Persona } from "./access";
import { routeReadAllowed } from "./routeTable";
import { resolveLanding } from "./navProfiles";
import { ensureTenantContext, resetTenantContext } from "../lib/tenant-vocab";
import { RAIL_ROUTES } from "./chrome-rail";
import { getDemoTenants, type DemoTenant, type DemoPersonaDto } from "../screens/workforce/wf-api";
import { fetchTenantTheme } from "@v0y4ge/api-client";
import { useNav, isKnownRoute } from "./router";
import { useViewport } from "./useResponsive";
import { availableLocales, currentLocale, localeDisplayName, refreshTranslationCatalog, setAppLocale, useT, t as i18nT } from "../lib/i18n";

const SANS = "var(--jrni-font-family-sans)";
const ASSET_BASE = (window as unknown as { __STUDIO_ASSET_BASE__?: string }).__STUDIO_ASSET_BASE__ ?? "";
// The host owns the real (non-demo) sign-in page and injects its URL. There is
// deliberately NO default: a guessed path would strand the user exactly the way
// the empty persona grid did.
const REAL_SIGNIN_URL = (window as unknown as { __STUDIO_SIGNIN_URL__?: string }).__STUDIO_SIGNIN_URL__ ?? "";
// Softer brand: lavender wash, not a slab of dark purple.
// (CC, 2026-10-05) Theme surface token: the old light-only gradient and #fff cards sat under theme-flipping text, so dark mode rendered light text on white.
const WASH = T.canvas;

const INDUSTRY_LABEL: Record<string, string> = { events: "Events", banking: "Banking", "higher-ed": "Higher education", retail: "Retail" }; // ui-config

type SigninPersona = Persona & { locale?: string; assignments?: Partial<Record<string, string>> };
// Process-role chips (2026-08-10): personas carry a role PER PROCESS in
// `assignments` (seed-world/<tenant>/personas.json), but the card only ever
// showed the primary `role` label — so a Branch Manager who is also the tenant's
// Event Manager read as having no events access at all. Chips make the full
// cross-process assignment legible at the chooser. Scope pins (regionId/branchId)
// are not roles and are filtered out. ui-config.
const PROCESS_LABEL: Record<string, string> = { events: "Events", workforce: "Workforce", queues: "Queues", storeops: "Store ops", appointments: "Appointments" };
function assignmentChips(a?: Partial<Record<string, string>>): { process: string; role: string }[] {
  if (!a) return [];
  return Object.keys(PROCESS_LABEL)
    .filter((k) => typeof a[k] === "string" && a[k])
    .map((k) => ({ process: PROCESS_LABEL[k], role: a[k] as string }));
}
// Locale → representative flag region for the chooser pill (demo affordance;
// bare tags per the kit's demo-personas fixture). An explicit region subtag
// wins (de-CH→CH, zh-HK→HK). ui-config.
const LOCALE_REGION: Record<string, string> = { en: "US", fr: "FR", de: "DE", hi: "IN", ja: "JP", es: "ES", ko: "KR", ar: "SA", it: "IT", pt: "BR", zh: "HK" };
function flagOf(tag: string): string {
  const [lang, region] = tag.split("-");
  const r = (region && region.length === 2 ? region : LOCALE_REGION[lang]) || "US";
  return String.fromCodePoint(...[...r.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

function toPersona(d: DemoPersonaDto): SigninPersona {
  const name = d.displayName ?? d.name ?? d.email ?? "";
  return { id: d.id, name, email: d.email ?? "", role: d.role ?? "", init: initialsOf(name), color: colorFor(d.id ?? name), blurb: d.blurb ?? "", locale: d.locale ?? "en", assignments: d.assignments, photoUrl: safeImageUrl(d.photoUrl) };
}

/* The sign-in mark is the tenant's own logo (CC, 2026-10-04), read from the public theme (no sign-in needed;
   the same read the theme bridge makes at boot). The JRNI mark shows while it loads, when the tenant has no
   logo, and when the logo does not load. */
export function Mark() {
  const t = useT();
  const base = (window as unknown as { __STUDIO_ASSET_BASE__?: string }).__STUDIO_ASSET_BASE__ ?? "";
  const [logo, setLogo] = useState<string | null>(null);
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    let live = true;
    Promise.resolve().then(() => fetchTenantTheme()).then((theme) => { if (live) setLogo(theme?.brand?.logoUrl || null); }).catch(() => {});
    return () => { live = false; };
  }, []);
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
      {logo && !broken
        ? <img src={logo} alt={t("chrome.brand.workspace", "Workspace")} onError={() => setBroken(true)} style={{ height: 30, maxWidth: 180, width: "auto", objectFit: "contain", display: "block" }} />
        : <img src={`${base}/jrni-logo.png`} alt={t("shell.signin.jrni", "JRNI")} style={{ height: 30, width: "auto", display: "block" }} />}
      <span style={{ fontSize: 19, fontWeight: 500, color: T.n2, letterSpacing: -0.3, fontFamily: SANS, borderLeft: `1px solid ${T.border}`, paddingLeft: 10 }}>{t("shell.signin.studio", "Studio")}</span>
    </div>
  );
}

/** An image over the initials beneath it (CC, 2026-10-05): tries each source in turn, moving to the next when
 *  one fails to load, and shows nothing once they are exhausted, so the initials show through. */
function PersonaPhoto({ sources }: { sources: (string | null | undefined)[] }) {
  const list = sources.filter((s): s is string => !!s);
  const [i, setI] = useState(0);
  const key = list.join("|");
  useEffect(() => { setI(0); }, [key]);
  if (i >= list.length) return null;
  return <img key={list[i]} src={list[i]} alt="" aria-hidden onError={() => setI((n) => n + 1)} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />;
}

/** The organisation's mark (CC, 2026-10-05): its logo on white when /demo/tenants carries a `logoUrl`, the
 *  initials tile otherwise or when the logo fails to load. */
function OrgMark({ tenant, size, radius, fontSize }: { tenant: DemoTenant; size: number; radius: number; fontSize: number }) {
  const logo = safeImageUrl(tenant.logoUrl);
  const [broken, setBroken] = useState<string | null>(null);
  const showLogo = !!logo && broken !== logo;
  return (
    <span style={{ width: size, height: size, borderRadius: radius, flexShrink: 0, overflow: "hidden", display: "inline-flex", alignItems: "center", justifyContent: "center",
      background: showLogo ? "#FFFFFF" : (tenant.brandColor || T.p1), border: showLogo ? `1px solid ${T.border}` : "none", boxSizing: "border-box",
      color: "#fff", fontWeight: 800, fontSize }}>
      {showLogo
        ? <img src={logo!} alt="" aria-hidden onError={() => setBroken(logo)} style={{ width: "82%", height: "82%", objectFit: "contain" }} />
        : (tenant.initials || initialsOf(tenant.name))}
    </span>
  );
}

function PersonaCard({ p, orgs, onPick, signing, busy }: { p: SigninPersona; orgs?: number; onPick: (p: SigninPersona) => void; signing?: boolean; busy?: boolean }) {
  const t = useT();
  const chips = assignmentChips(p.assignments);
  return (
    // ui-raw: persona choice tile (avatar + two-line label + hover elevation) — ChoiceTile/SelectableTile's role, not Button's.
    <button data-testid="signin-persona-pick" data-id={p.id} onClick={() => onPick(p)}
      disabled={busy} aria-busy={signing || undefined}
      /* TOP-ALIGNED (operator, 2026-09-17): a card's height is set by its blurb
         and its chip rows, so centring floated the portrait to the middle of a
         two-line card and the middle of a four-line one — the discs never lined
         up across a row. Aligned to the top, every portrait sits on the name's
         line whatever the card below it says. */
      style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "14px 16px", borderRadius: 12, border: `1px solid ${signing ? p.color : T.border}`, background: T.white, cursor: busy ? "default" : "pointer", textAlign: "left", fontFamily: SANS, transition: "border-color 120ms, box-shadow 120ms, opacity 120ms", boxShadow: signing ? T.shadowMd : "none", opacity: busy && !signing ? 0.45 : 1 }}
      onMouseEnter={(e) => { if (busy) return; e.currentTarget.style.borderColor = p.color; e.currentTarget.style.boxShadow = T.shadowMd; }}
      onMouseLeave={(e) => { if (busy) return; e.currentTarget.style.borderColor = T.border; e.currentTarget.style.boxShadow = "none"; }}>
      {/* Squared with a small radius, matching the signed-in tile in the top bar
          (2026-09-17): the same face should not be a circle on the way in and a
          rounded square once you are in. 7px at 46 is the top bar's 5-at-36
          proportion. */}
      <span style={{ position: "relative", width: 46, height: 46, marginTop: 1, borderRadius: 7, background: p.color, color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 17, fontWeight: 700, flexShrink: 0, overflow: "hidden" }}>
        {p.init}
        {/* A REAL HEADSHOT OR NOTHING. `app/public/avatars/<id>.png` held
            generated grey silhouettes — the same anonymous blob on every card,
            which read as a broken image beside the two personas that had no file
            and so showed clean initials. All 24 were deleted; one tenant's
            eight now carry real headshots (operator-supplied, 2026-09-17).
            Everyone else falls back to the initials disc in their own colour,
            which is an honest placeholder rather than an anonymous face. Drop a
            photo at that path and it takes over, file by file, no code change.
            `onError` reveals the initials beneath. */}
        {/* The persona's own photo first (CC, 2026-10-05: /demo/personas `photoUrl`, from the staff record), then
            the static file, then the initials. Each `onError` moves to the next. */}
        <PersonaPhoto sources={[p.photoUrl, `${ASSET_BASE}/avatars/${p.id}.png`]} />
        {/* No per-card spinner (2026-09-24, PP): three loaders at once — this
            one, the inline status below and the centred step dialog — read as
            three separate waits. The centred ProgressSpinner is the single
            loader; the pressed card keeps its highlighted border. */}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 15.5, fontWeight: 700, color: T.n1 }}>{p.name}</span>
        <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: p.color, margin: "1px 0 3px" }}>{p.role}{orgs && orgs > 1 ? <span style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 700, color: T.n2, background: T.n6, borderRadius: 999, padding: "2px 8px", verticalAlign: "1px" }}>{orgs} {t("shell.signin.organizations", "organizations")}</span> : null}</span>
        <span style={{ display: "block", fontSize: 12.5, color: T.soft, lineHeight: 1.45 }}>{p.blurb}</span>

        {chips.length ? (
          <span style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 7 }}>
            {chips.map((c) => (
              <span key={c.process} style={{ display: "inline-flex", alignItems: "baseline", gap: 4, fontSize: 10.5, lineHeight: 1.5, color: T.n2, background: T.n6, border: `1px solid ${T.border}`, borderRadius: 999, padding: "2px 8px", whiteSpace: "nowrap" }}>
                <span style={{ color: T.soft, fontWeight: 600 }}>{c.process}</span>
                <span style={{ fontWeight: 700 }}>{c.role}</span>
              </span>
            ))}
          </span>
        ) : null}
      </span>
      {p.locale ? (
        <span title={localeDisplayName(p.locale)} aria-label={localeDisplayName(p.locale)} style={{ display: "inline-flex", alignItems: "center", gap: 5, flexShrink: 0, fontSize: 10.5, fontWeight: 700, color: T.n2, background: T.n6, borderRadius: 999, padding: "3px 8px" }}>
          <span style={{ fontSize: 13, lineHeight: 1 }} aria-hidden>{flagOf(p.locale)}</span>{p.locale.toUpperCase()}
        </span>
      ) : null}
      <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke={T.n3} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden><path d="m6 4 4 4-4 4" /></svg>
    </button>
  );
}

/** CC 0191: the host shell's demo check (GET /t/<tenant>/demo/personas) failed
 *  after its retries. Not "demo is off" (that is a 404 or an empty list), so the
 *  screen says sign-in can't be reached and offers Retry, never the real login.
 *  `onRetry` may return a promise; the button stays busy until it settles. */
export interface SignInScreenProps {
  demoUnreachable?: boolean;
  onRetry?: () => void | Promise<unknown>;
}

function UnreachablePanel({ onRetry }: { onRetry?: () => void | Promise<unknown> }) {
  const [busy, setBusy] = useState(false);
  const [tries, setTries] = useState(0);
  const run = async () => {
    if (busy || !onRetry) return;
    setBusy(true);
    try { await onRetry(); } catch { /* the shell decides what comes next */ }
    finally { setBusy(false); setTries((n) => n + 1); }
  };
  return (
    <div role="alert" data-testid="signin-unreachable" style={{ padding: "28px 22px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
      <span aria-hidden style={{ width: 44, height: 44, borderRadius: 999, background: "#FFF8E6", border: "1px solid #F2D58A", color: "#8A5A00", display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Icon.warn size={18} /></span>
      <div style={{ fontSize: 16, fontWeight: 700, color: T.n1 }}>We couldn't reach sign-in</div>
      <p style={{ margin: 0, fontSize: 13.5, color: T.soft, lineHeight: 1.55, maxWidth: 400, textWrap: "pretty" } as CSSProperties}>
        The sign-in service didn't answer in time, so we can't show the demo roles yet. This is usually brief. Nothing is wrong with your account.
      </p>
      <div style={{ marginTop: 6, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
        <Btn variant="primary" onClick={() => { void run(); }} disabled={busy || !onRetry} testid="signin-unreachable-retry">
          {busy ? "Trying again…" : "Try again"}
        </Btn>
        <span aria-live="polite" style={{ fontSize: 12, color: T.soft, minHeight: 16 }}>
          {busy ? "Checking sign-in…" : tries > 0 ? "Still can't reach sign-in. Wait a minute, then try again." : ""}
        </span>
      </div>
    </div>
  );
}

export default function Signin({ demoUnreachable = false, onRetry, mode = "page" }: SignInScreenProps & { mode?: "page" | "reauth" } = {}) {
  const expiry = getSessionExpiry();
  /* In a dialog over the screen whose session ended with unsaved changes (CC 0306). */
  const reauth = mode === "reauth" ? getReauth() : null;
  const t = useT();
  const translate = t;
  const nav = useNav();
  const vp = useViewport();
  const [tenants, setTenants] = useState<DemoTenant[] | null>(null);   // null = loading / unavailable
  /* null = the fallback persona read has not answered yet; [] = it answered and
   * there is nothing to choose. Collapsing the two rendered an empty grid under
   * "Sign in to continue" — a screen with no way forward and no explanation. */
  const [flat, setFlat] = useState<Persona[] | null>(null);            // fallback: flat persona list
  const [tenantsFailed, setTenantsFailed] = useState(false);
  const [active, setActive] = useState<DemoTenant | null>(null);       // ui-local (chooser step)
  const [signing, setSigning] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);                           // ui-local (retry)
  /* Sign-in sequencing (2026-09-17). AuthGate keeps this screen mounted while
   * `resolvingLanding` is set, so the overlay below is what the user sees from
   * card press to landing — never <AppRouter/> at the boot default, never the
   * launcher's empty state. `phase` drives the step counter; `stuck` is the
   * retry state for a composition fetch that failed outright. */
  const { resolvingLanding } = useAuth();
  const [phase, setPhase] = useState(0);                               // 0 signing · 1 workspace · 2 opening
  const [landingName, setLandingName] = useState<string | null>(null);
  const [stuck, setStuck] = useState<{ p: SigninPersona; fallback: string } | null>(null);
  /* A failed demo sign-in (CC 0210/0211): signIn() rethrows; the chooser stays and says so. */
  const [signInFailed, setSignInFailed] = useState(false);
  /* THE ADDRESS THE USER WAS ON (2026-10-02). When a session lapses, this screen
   * renders at the page the user was working on — /workforce/myschedule?view=month,
   * say — and the router still holds that route. Re-signing in used to throw it
   * away and land on the role's home (My Day). Read from the router at the
   * moment of sign-in, not at mount: Sign out mounts this screen a beat before it
   * resets the route to "signin", and a mount-time copy would carry the previous
   * person's page into the next person's session. The chooser's own steps never
   * touch the router, so they cannot change it. Honoured only for a route that
   * exists and that the NEW session may open; "signin" (an explicit sign-out)
   * and "dashboard" (the bare root, which is the boot default) mean "no deep
   * link" and keep the served landing. */
  const deepLinkFor = (): { route: string; params: Record<string, unknown> } | null => {
    const r = nav.route;
    if (!r || r === "signin" || r === "dashboard" || !isKnownRoute(r)) return null;
    return routeReadAllowed(r, can) ? { route: r, params: nav.params } : null;
  };

  /* While the shell reports sign-in unreachable (CC 0194), make no demo reads: the
     service just failed. They run once demoUnreachable clears. */
  useEffect(() => {
    if (demoUnreachable) return;
    let live = true;
    getDemoTenants()
      .then((r) => { if (live && Array.isArray(r?.tenants) && r.tenants.length) setTenants(r.tenants); else if (live) setTenantsFailed(true); })
      .catch(() => { if (live) setTenantsFailed(true); });
    return () => { live = false; };
  }, [attempt, demoUnreachable]);
  useEffect(() => {
    if (!tenantsFailed || demoUnreachable) return;
    let live = true;
    fetchPersonas().then((ps) => { if (live) setFlat(ps); });
    return () => { live = false; };
  }, [tenantsFailed, attempt, demoUnreachable]);

  const retry = () => { setTenants(null); setTenantsFailed(false); setFlat(null); setAttempt((n) => n + 1); };

  /* Resolve and apply the landing route. Split from `pick()` so the overlay's
   * Retry can re-run just this half after a failed composition fetch. Returns
   * false when it stopped short (the caller keeps the overlay up with a retry). */
  const land = async (p: SigninPersona): Promise<"landed" | "stuck"> => {
    /* Re-sign-in over the screen (CC 0306). The same person: close the dialog, nothing navigates, and
       the typed work is still there to save. Someone else: their landing, and the screen state is dropped. */
    if (mode === "reauth") {
      const who = finishReauth();
      if (who === "same") { toast({ tone: "success", title: "You're signed in again", subtitle: "Your changes are still here. Save when you're ready." }); return "landed"; }
    }
    setPhase(1);
    const u = getUser();
    const r = await resolveLanding(u, p.role);
    /* After an expired session, return to the screen the person was on (CC 0176). */
    const ret = takeSessionReturn();
    /* The person signing in may not be the one whose session expired (CC 0182): the
       saved screen is used only if THIS person may open it; otherwise their own
       deep link or landing applies. */
    const retOk = !!ret && isKnownRoute(ret.route) && routeReadAllowed(ret.route, can);
    const deep = (retOk ? ret : null) ?? deepLinkFor();
    if (r.failed && !deep) { setStuck({ p, fallback: r.route }); return "stuck"; }
    const target = deep ? deep.route : r.route;
    const item = r.items.find((i) => RAIL_ROUTES[i.id] === target || i.id === target);
    setLandingName(item?.label ?? null);
    setPhase(2);
    if (deep) nav.reset(deep.route, deep.params);
    else nav.reset(r.route);
    return "landed";
  };

  const pick = async (p: SigninPersona) => {
    if (signing) return;
    setSigning(p.id); setPhase(0); setLandingName(null); setStuck(null); setSignInFailed(false);
    let outcome: "landed" | "stuck" | "threw" = "threw";
    try {
      try { await signIn(p.id, p.photoUrl); }
      catch { setSignInFailed(true); return; }
      /* Start the tenant vocabulary + nav-composition fetch NOW, so it overlaps
       * the locale work below instead of being kicked off by the landing
       * resolution itself (docket 2026-09-16, Ask 3). */
      void ensureTenantContext();
      // Persona-locale-session parity (kit demo semantics): the persona's saved
      // language preference (their user record) applies at sign-in — it's what
      // the chooser's flag pill promises. ui-local render config; in-session
      // changes + persistence live on Account details → Profile → Language.
      const tag = p.locale ?? "en";
      if (availableLocales().includes(tag) && tag !== currentLocale()) {
        setAppLocale(tag);
        void refreshTranslationCatalog(getActiveOrganizationId());
      }
      // Overnight docket (2026-07-28): a served nav profile's landing route
      // (getNavComposition) takes precedence when one resolves for this user;
      // the per-process assignment landing is the pre-profile fallback.
      outcome = await land(p);
    } finally {
      setSigning(null);
      /* Clear the gate here, in `finally`, so a thrown profile fetch can never
       * strand the user behind the overlay. When `land()` stopped on a failed
       * fetch the gate stays up ON PURPOSE — the overlay now shows Retry. */
      if (outcome !== "stuck") landingResolved();
    }
  };

  const retryLanding = async () => {
    if (!stuck) return;
    const p = stuck.p;
    setStuck(null); setSigning(p.id);
    resetTenantContext();
    void ensureTenantContext();
    let outcome: "landed" | "stuck" | "threw" = "threw";
    try { outcome = await land(p); }
    finally { setSigning(null); if (outcome !== "stuck") landingResolved(); }
  };
  const continueAnyway = () => {
    if (!stuck) return;
    const route = stuck.fallback;
    setStuck(null); setPhase(2);
    nav.reset(route);
    landingResolved();
  };
  const abandonSignIn = () => { setStuck(null); landingResolved(); };

  const grouped = tenants && !tenantsFailed;
  const heading = demoUnreachable ? "Sign in to continue" : grouped && !active ? "Choose an organization" : "Sign in to continue";
  const sub = demoUnreachable
    ? "Demo sign-in lets you try Studio as any role."
    : grouped && !active
    ? t("shell.signin.chooseOrganizationSubtitle", "Each demo organization has its own apps and people, tuned to its industry.")
    : "Choose a role to sign in as. You'll see exactly the apps and tools that role is enabled for.";

  return (
    <div style={{ height: "100%", maxHeight: "100vh", background: WASH, display: "flex", flexDirection: "column", alignItems: "center", padding: vp === "phone" ? "0" : 24, fontFamily: SANS, overflowY: "auto" }}>
      {/* margin:auto (not justify-content:center) so centering stays overflow-safe: on short viewports the column top remains reachable and the page scrolls. */}
      <div style={{ width: "100%", maxWidth: vp === "desktop" ? 900 : 460, display: "flex", flexDirection: "column", gap: 0, margin: vp === "phone" ? "0 auto" : "auto" }}>
        {/* Header */}
        <div style={{ padding: vp === "phone" ? "40px 22px 20px" : "8px 8px 26px", textAlign: vp === "phone" ? "left" : "center", display: "flex", flexDirection: "column", alignItems: vp === "phone" ? "flex-start" : "center", gap: 14 }}>
          <Mark />
          <div>
            <h1 style={{ margin: 0, fontSize: vp === "phone" ? 26 : 30, fontWeight: 700, color: T.n1, letterSpacing: -0.5, lineHeight: 1.15 }}>{heading}</h1>
            <p style={{ margin: "8px 0 0", fontSize: 15, color: T.soft, lineHeight: 1.5, maxWidth: 460 }}>{sub}</p>
          </div>
        </div>

        {/* Expired-session notice (CC 0176). One notice however many calls failed. */}
        {reauth ? (
          <div role="status" data-testid="signin-reauth" style={{ margin: vp === "phone" ? "0 22px 16px" : "0 0 16px", padding: "12px 16px", borderRadius: 12, background: "#FFF8E6", border: "1px solid #F2D58A", display: "flex", gap: 10, alignItems: "flex-start" }}>
            <span style={{ color: "#8A5A00", display: "inline-flex", marginTop: 2 }}><Icon.warn size={15} /></span>
            <div style={{ fontSize: 14, color: T.n1, lineHeight: 1.5 }}>
              <div style={{ fontWeight: 600 }}>Your session has ended. Sign in again to keep working.</div>
              <div style={{ marginTop: 2 }}>Anything you typed is still on the screen behind this. If someone else signs in, it's discarded.</div>
            </div>
          </div>
        ) : expiry ? (
          <div role="status" data-testid="signin-session-expired" style={{ margin: vp === "phone" ? "0 22px 16px" : "0 0 16px", padding: "12px 16px", borderRadius: 12, background: "#FFF8E6", border: "1px solid #F2D58A", display: "flex", gap: 10, alignItems: "flex-start" }}>
            <span style={{ color: "#8A5A00", display: "inline-flex", marginTop: 2 }}><Icon.warn size={15} /></span>
            <div style={{ fontSize: 14, color: T.n1, lineHeight: 1.5 }}>
              <div style={{ fontWeight: 600 }}>Your session has expired. Sign in again to continue.</div>
              {expiry.unsavedChanges ? <div style={{ marginTop: 2 }}>Your changes were not saved.</div> : null}
            </div>
          </div>
        ) : null}

        {/* Card panel */}
        <div style={{
          background: vp === "phone" ? "transparent" : T.white,
          borderRadius: vp === "phone" ? 0 : 16,
          border: vp === "phone" ? "none" : `1px solid ${T.border}`,
          boxShadow: vp === "phone" ? "none" : T.shadowLg,
          padding: vp === "phone" ? "0 14px 28px" : 16,
          display: "flex", flexDirection: "column", gap: 10,
        }}>
          {signInFailed && !demoUnreachable ? (
            <div role="alert" data-testid="signin-failed" style={{ display: "flex", alignItems: "center", gap: 8, margin: "0 4px 12px", padding: "10px 12px", borderRadius: 10, background: T.red2, color: T.red1, fontSize: 13, fontWeight: 600 }}>
              <Icon.warn size={14} /> <span>{t("shell.signin.couldntSignIn", "Couldn't sign in. Try again.")}</span>
            </div>
          ) : null}
          {demoUnreachable ? (
            <UnreachablePanel onRetry={onRetry} />
          ) : grouped && active ? (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "2px 4px 8px" }}>
                <IconBtn size={32} testid="signin-tenant-back" disabled={!!signing} onClick={() => setActive(null)} label={t("shell.signin.backToOrganizations", "Back to organizations")}><Icon.arrow dir="left" size={13} /></IconBtn>
                <OrgMark size={34} radius={9} fontSize={14} tenant={active} />
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 15, fontWeight: 700, color: T.n1 }}>{active.name}</span>
                  <span style={{ display: "block", fontSize: 12, color: T.soft }}>{INDUSTRY_LABEL[active.industry] ?? active.industry}</span>
                </span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: vp === "desktop" ? "1fr 1fr" : "1fr", gap: 10 }}>
                {active.personas.map((d) => { const per = toPersona(d); return <PersonaCard key={d.id} p={per} orgs={d.workspaces ? d.workspaces.length : undefined} onPick={pick} signing={signing === per.id} busy={!!signing} />; })}
              </div>
            </>
          ) : grouped ? (
            <div style={{ display: "grid", gridTemplateColumns: vp === "desktop" ? "1fr 1fr 1fr" : "1fr", gap: 10 }}>
              {(tenants ?? []).map((t) => (
                // ui-raw: organisation choice tile — same reason as the persona tile above.
                <button key={t.id} data-testid="signin-tenant-pick" data-id={t.id} onClick={() => setActive(t)}
                  style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 10, padding: "18px 16px", borderRadius: 12, border: `1px solid ${T.border}`, background: T.white, cursor: "pointer", textAlign: "left", fontFamily: SANS, transition: "border-color 120ms, box-shadow 120ms" }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = t.brandColor || T.p1; e.currentTarget.style.boxShadow = T.shadowMd; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.boxShadow = "none"; }}>
                  <OrgMark size={44} radius={11} fontSize={16} tenant={t} />
                  <span>
                    <span style={{ display: "block", fontSize: 15.5, fontWeight: 700, color: T.n1 }}>{t.name}</span>
                    <span style={{ display: "block", fontSize: 12, fontWeight: 600, color: t.brandColor || T.p1, margin: "1px 0 4px" }}>{INDUSTRY_LABEL[t.industry] ?? t.industry}</span>
                    <span style={{ display: "block", fontSize: 12.5, color: T.soft, lineHeight: 1.45 }}>{t.blurb}</span>
                  </span>
                  <span style={{ fontSize: 11.5, color: T.soft, display: "inline-flex", alignItems: "center", gap: 5 }}><Icon.users size={12} /> {t.personas.length} {translate("shell.signin.people", "people")}</span>
                </button>
              ))}
            </div>
          ) : tenantsFailed && flat && flat.length ? (
            <div style={{ display: "grid", gridTemplateColumns: vp === "desktop" ? "1fr 1fr" : "1fr", gap: 10 }}>
              {flat.map((p) => <PersonaCard key={p.id} p={p} onPick={pick} signing={signing === p.id} busy={!!signing} />)}
            </div>
          ) : tenantsFailed && flat ? (
            /* Neither /demo/tenants nor /demo/personas returned anything to pick:
             * this tenant has no demo mode. Say so, and point at the sign-in that
             * does work — a sign-in screen that cannot offer a way to proceed has
             * to admit it. */
            <div role="status" style={{ padding: "26px 22px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
              <span style={{ width: 40, height: 40, borderRadius: 999, background: T.n6, color: T.n2, display: "inline-flex", alignItems: "center", justifyContent: "center" }} aria-hidden><Icon.lock size={16} /></span>
              <div style={{ fontSize: 15.5, fontWeight: 700, color: T.n1 }}>{t("shell.signin.demoUnavailableTitle", "Demo sign-in isn't available here")}</div>
              <p style={{ margin: 0, fontSize: 13.5, color: T.soft, lineHeight: 1.5, maxWidth: 380 }}>
                {t("shell.signin.demoUnavailableBody", "This organization doesn't have demo mode enabled, so there are no roles to sign in as. Use your organization's sign-in page instead.")}
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center", marginTop: 4 }}>
                {REAL_SIGNIN_URL ? (
                  // ui-raw: single link-as-button on the unauthenticated screen.
                  <a href={REAL_SIGNIN_URL} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 16px", borderRadius: 10, background: T.p1, color: "#fff", fontSize: 13.5, fontWeight: 700, textDecoration: "none", fontFamily: SANS }}>
                    {t("shell.signin.goToSignIn", "Go to sign-in")}
                  </a>
                ) : null}
                <button onClick={retry} style={{ padding: "9px 16px", borderRadius: 10, border: `1px solid ${T.border}`, background: T.white, color: T.n1, fontSize: 13.5, fontWeight: 700, cursor: "pointer", fontFamily: SANS }}>
                  {t("shell.signin.tryAgain", "Try again")}
                </button>
              </div>
            </div>
          ) : (
            /* Busy pattern for a first read past ~500 ms (2026-09-17): the demo
             * tenant list is a network call, and a static "Loading…" line reads
             * as a stalled page once it runs long. */
            <div aria-busy="true" style={{ minHeight: 160 }}>
              <div data-testid="signin-options-busy" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "24px 0" }}>
            {/* Only a spinner here, so jrni-ui's Spinner (CC 0068 item 24). */}
            <Spinner size="lg" label={t("shell.signin.loadingSignInOptions", "Loading sign-in options…")} />
            <span aria-hidden="true" style={{ fontSize: 13, color: T.soft }}>{t("shell.signin.loadingSignInOptions", "Loading sign-in options…")}</span>
          </div>
            </div>
          )}
        </div>

        <p style={{ textAlign: "center", fontSize: 12, color: T.soft, margin: "20px 0 28px", padding: "0 22px" }}>
          {i18nT("shell.signin.demoSignInInProductionThisIsYourEmailAmpPasswordOr.rendered", "Demo sign-in. In production this is your email & password or single sign-on.")}
        </p>
      </div>

      {/* Full-screen busy overlay, card press → landing (2026-09-17). Visible for
          the whole of sign-in, the composition fetch and the route change; the
          third step names the landing once the served rail knows it. On a failed
          composition fetch it becomes the retry state instead of silently
          landing the persona somewhere their role does not map to. */}
      {signing || resolvingLanding || stuck ? (
        <ProgressSpinner
          variant="overlay"
          active
          testid="signin-landing-overlay"
          minDisplayMs={400}
          label={stuck ? t("shell.signin.workspaceUnavailable", "We couldn't load your workspace") : undefined}
          steps={stuck ? undefined : [
            { label: t("shell.signin.stepSigningIn", "Signing you in") },
            { label: t("shell.signin.stepLoadingWorkspace", "Loading your workspace") },
            { label: landingName ? `${t("shell.signin.stepOpening", "Opening")} ${landingName}` : t("shell.signin.stepOpeningWorkspace", "Opening your workspace") },
          ]}
          currentStep={phase}
        >
          {stuck ? (
            <>
              <Btn variant="primary" size="sm" onClick={retryLanding} testid="signin-landing-retry">{t("shell.signin.tryAgain", "Try again")}</Btn>
              <Btn variant="secondary" size="sm" onClick={continueAnyway} testid="signin-landing-continue">{t("shell.signin.continueAnyway", "Continue anyway")}</Btn>
              <Btn variant="ghost" size="sm" onClick={abandonSignIn} testid="signin-landing-back">{t("shell.signin.backToSignIn", "Back")}</Btn>
            </>
          ) : null}
        </ProgressSpinner>
      ) : null}
    </div>
  );
}
